import { DfSelectSingleOption } from '../../../shared/_directives/df-select-single/df-select-single-filter.model';
import { ReturnReviewStep } from './efile-workflow.service';

/** One row in the Return Review Dashboard consolidated table. */
export interface ReturnReviewDashboardRow {
  step: ReturnReviewStep;
  screenName: string;
  errorStatus: 'Error' | 'No Errors';
  reviewStatus: string;
  reviewerComments: string;
  reviewedTimestamp: string;
  outstanding: boolean;
}

/** Column metadata for `multiGridExportToExcel` (Efile_Return_Review_Export). */
export const RETURN_REVIEW_DASHBOARD_EXPORT_HEADERS = [
  { object_id: '1', label: 'Screen Name', data_type: 'string', format: null, map: 'STEP_NAME', width: 220 },
  { object_id: '2', label: 'Error Status', data_type: 'string', format: null, map: 'ERROR_STATUS', width: 120 },
  { object_id: '3', label: 'Review Status', data_type: 'string', format: null, map: 'STEP_STATUS', width: 120 },
  { object_id: '4', label: 'Reviewer Comments', data_type: 'string', format: null, map: 'EXPLANATION', width: 280 },
  { object_id: '5', label: 'Reviewed Timestamp', data_type: 'string', format: null, map: 'UPDATED_ON', width: 160 },
] as const;

export function mapStepToDashboardRow(step: ReturnReviewStep): ReturnReviewDashboardRow {
  const status = (step.STEP_STATUS || 'PENDING').toUpperCase();
  return {
    step,
    screenName: (step.STEP_NAME || '').trim(),
    errorStatus: resolveErrorStatus(step, status),
    reviewStatus: formatReviewStatus(status),
    reviewerComments: resolveReviewerComments(step),
    reviewedTimestamp: resolveReviewedTimestamp(step, status),
    outstanding: status === 'PENDING' || status === 'FAILED',
  };
}

export function mapStepsToDashboardRows(steps: ReturnReviewStep[]): ReturnReviewDashboardRow[] {
  return (steps || []).map(mapStepToDashboardRow);
}

const IRS_SUBMISSION_DATE_KEYS = [
  'SUBMITTED_DATE',
  'SUBMITTED_ON',
  'SUBMITTED_TS',
  'CREATED_ON',
  'CREATED_DATE',
  'CREATED_TS',
  'UPDATED_ON',
  'UPDATED_TS',
  'IRS_SUBMITTED_DATE',
  'SUBMISSION_DATE',
  'DATE_CREATED',
];

/**
 * Picks the newest IRS submission from `j4k0an` options: max date field when
 * present, else max numeric `VALUE`, else first row (API sort).
 */
export function pickLatestIrsSubmissionOption(
  rows: DfSelectSingleOption[],
): DfSelectSingleOption | null {
  if (!rows?.length) return null;

  let bestByDate: DfSelectSingleOption | null = null;
  let bestTime = -Infinity;
  for (const row of rows) {
    for (const key of IRS_SUBMISSION_DATE_KEYS) {
      const raw = row[key];
      if (raw == null || String(raw).trim() === '') continue;
      const time = new Date(String(raw)).getTime();
      if (!Number.isNaN(time) && time > bestTime) {
        bestTime = time;
        bestByDate = row;
      }
    }
  }
  if (bestByDate) return { ...bestByDate };

  let bestById: DfSelectSingleOption | null = null;
  let maxId = -Infinity;
  for (const row of rows) {
    const n = Number(row.VALUE);
    if (!Number.isNaN(n) && n > maxId) {
      maxId = n;
      bestById = row;
    }
  }
  if (bestById) return { ...bestById };

  return { ...rows[0] };
}

/** Keep a user-picked `irs_id` when still in the list; otherwise default to newest. */
export function resolveDashboardIrsSelected(
  rows: DfSelectSingleOption[],
  preserveIrsId?: string,
): DfSelectSingleOption[] {
  if (!rows.length) return [];
  const prior = String(preserveIrsId ?? '').trim();
  if (prior) {
    const match = rows.find((r) => String(r.VALUE) === prior);
    if (match) return [{ ...match }];
  }
  const latest = pickLatestIrsSubmissionOption(rows);
  return latest ? [latest] : [];
}

function resolveErrorStatus(step: ReturnReviewStep, status: string): 'Error' | 'No Errors' {
  const explicit = String(
    step.ERROR_STATUS ?? step.ERROR_STATUS_LABEL ?? step.HAS_ERRORS ?? '',
  )
    .trim()
    .toUpperCase();

  if (explicit === 'ERROR' || explicit === 'Y' || explicit === 'YES' || explicit === '1') {
    return 'Error';
  }
  if (
    explicit === 'NO ERRORS' ||
    explicit === 'NO_ERROR' ||
    explicit === 'N' ||
    explicit === 'NO' ||
    explicit === '0'
  ) {
    return 'No Errors';
  }
  return status === 'FAILED' ? 'Error' : 'No Errors';
}

function formatReviewStatus(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'RESOLVED':
      return 'Resolved';
    case 'IGNORED':
      return 'Ignored';
    case 'FAILED':
      return 'Failed';
    default:
      if (!status) return 'Pending';
      return status.charAt(0) + status.slice(1).toLowerCase();
  }
}

function resolveReviewerComments(step: ReturnReviewStep): string {
  const raw =
    step.REVIEWER_COMMENTS ??
    step.COMMENTS ??
    step.COMMENT ??
    step.EXPLANATION ??
    pickFieldByPattern(step, /comment/i) ??
    '';
  return String(raw ?? '').trim();
}

/** Oracle `DATE`/`TIMESTAMP` serialized as `{ length, bytes, null }` from JDBC. */
interface OracleDateBytesPayload {
  length?: number;
  bytes?: string;
  null?: boolean;
}

function isOracleDateBytesPayload(value: unknown): value is OracleDateBytesPayload {
  return (
    value != null &&
    typeof value === 'object' &&
    typeof (value as OracleDateBytesPayload).bytes === 'string'
  );
}

/**
 * Decodes Oracle 7- or 11-byte TIMESTAMP (base64) to `YYYY-MM-DD HH:mm:ss.sss`.
 * Example `eH4FFggKKA==` → `2026-05-22 07:09:39.000`.
 */
function decodeOracleTimestampBytes(base64: string): string {
  try {
    const binary = atob(base64);
    if (binary.length < 7) return '';
    const b = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      b[i] = binary.charCodeAt(i);
    }
    const year = (b[0] - 100) * 100 + (b[1] - 100);
    const month = b[2];
    const day = b[3];
    const hour = b[4] - 1;
    const minute = b[5] - 1;
    const second = b[6] - 1;
    const pad2 = (n: number) => String(n).padStart(2, '0');
    let millis = 0;
    if (b.length >= 11) {
      millis = Math.floor(
        ((b[7] << 24) | (b[8] << 16) | (b[9] << 8) | b[10]) / 1_000_000,
      );
    }
    return `${year}-${pad2(month)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}:${pad2(second)}.${String(millis).padStart(3, '0')}`;
  } catch {
    return '';
  }
}

/** Normalizes API date values (plain string or Oracle bytes object). */
function coerceTimestampValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (isOracleDateBytesPayload(value)) {
    if (value.null) return '';
    const bytes = value.bytes?.trim();
    return bytes ? decodeOracleTimestampBytes(bytes) : '';
  }
  return '';
}

/** Reviewed Timestamp from `ws.UPDATED_ON` (siao78); Oracle bytes objects are decoded. */
function resolveReviewedTimestamp(step: ReturnReviewStep, status: string): string {
  const preferredKeys = [
    'UPDATED_ON',
    'UPDATED_TS',
    'STATUS_UPDATED_ON',
    'STATUS_UPDATED_TS',
    'LAST_UPDATED_ON',
    'REVIEWED_TS',
    'REVIEWED_TIMESTAMP',
    'REVIEWED_ON',
    'REVIEW_ON',
    'STEP_REVIEWED_ON',
    'IGNORED_ON',
    'IGNORED_TS',
    'RESOLVED_ON',
    'RESOLVED_TS',
    'LAST_REVIEWED_TS',
    'LAST_REVIEWED_ON',
    'LAST_REVIEWED',
    'MODIFIED_ON',
    'UPDATE_TS',
  ];

  for (const key of preferredKeys) {
    const value = readStepField(step, key);
    if (value) {
      return formatTimestampDisplay(value);
    }
  }

  const scanned = pickFieldByPattern(
    step,
    /(updated|reviewed|ignored|resolved|status.?updated).*(ts|on|date|timestamp)/i,
  );
  if (scanned) {
    return formatTimestampDisplay(scanned);
  }

  const updated = pickFieldByPattern(step, /^updated?_?(on|ts|date)$/i);
  if (updated && (status === 'RESOLVED' || status === 'IGNORED' || status === 'FAILED')) {
    return formatTimestampDisplay(updated);
  }

  return '';
}

function readStepField(step: ReturnReviewStep, key: string): string {
  const record = step as unknown as Record<string, unknown>;
  const direct = record[key];
  const coercedDirect = coerceTimestampValue(direct);
  if (coercedDirect) return coercedDirect;
  const lower = key.toLowerCase();
  for (const prop of Object.keys(record)) {
    if (prop.toLowerCase() === lower) {
      const coerced = coerceTimestampValue(record[prop]);
      if (coerced) return coerced;
    }
  }
  return '';
}

function pickFieldByPattern(step: ReturnReviewStep, pattern: RegExp): string {
  const record = step as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (!pattern.test(key)) continue;
    const coerced = coerceTimestampValue(value);
    if (coerced) return coerced;
  }
  return '';
}

/** Trim ISO strings for display; leave already-formatted values as-is. */
function formatTimestampDisplay(raw: string): string {
  const text = coerceTimestampValue(raw) || String(raw).trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}[T\s]/.test(text)) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
  }
  return text;
}
